import React, { useState, useEffect } from 'react';
import { ApolloProvider, ApolloClient } from '@apollo/client';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { loadTheme, mergeStyles } from '@fluentui/react';
import {
  FluentProvider,
  webLightTheme,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { CoherenceTheme } from '@coherence-design-system/styles';
import config, { loadConfiguration } from './common/ConfigLoader';
import App from './App';
import { IGraphUserObject } from '../utils/types/Users';
import graphApiRequest, { getAuthInstance, getClient, setAuthInstance } from '../utils/ApiProvider';
import { AuthModule } from '../utils/authentication/AuthModule';
import { setAppInsights } from '../utils/telemetry/appInsights';
import AuthorizationChecker from './authentication/AuthorizationChecker';
import StatHeader from './common/header/StatHeader';
import { IStatNotification, IStatNotificationItem, Toast } from './notifications/StatNotification';
import { NotificationProvider } from './notifications/NotificationContext';
import { graphConfig } from '../utils/AuthConfig';
import AuthCallback from './authentication/AuthCallback';
import Page from './common/page/Page';

// Lazy-loaded: renders the PowerBI-backed Stat Contacts report, so the PowerBI
// client is code-split out of the initial bundle and only loaded on that route.
const StatContactsNoAuthPage = React.lazy(() => import('./reports/StatContactsNoAuthPage'));

const useInitErrorStyles = makeStyles({
  container: {
    padding: '40px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  errorText: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalMNudge,
  },
});

const skipLinkStyle = mergeStyles({
  position: 'absolute',
  left: '-9999px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  zIndex: 9999,
  ':focus': {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 'auto',
    height: 'auto',
    padding: '8px 16px',
    background: '#fff',
    color: '#0078d4',
    fontSize: '14px',
    fontWeight: 600,
    border: '2px solid #0078d4',
    textDecoration: 'none',
    outline: 'none',
  },
});

/**
 * import the json file for config instead of fetching
 */
const AppLoader = (): JSX.Element => {
  const [client, setClient] = useState<ApolloClient<unknown>>();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [graphProfileData, setGraphProfileData] = useState<{
    userImageUrl: string | null;
    graphProfile: IGraphUserObject | null;
  }>({
    userImageUrl: null,
    graphProfile: null,
  });
  const [notifications, setNotifications] = useState<IStatNotificationItem[]>([]);
  const [statNotifications, setStatNotifications] = useState<IStatNotification[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [environmentName, setEnvironmentName] = useState('');
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    (async () => {
      try {
        await loadConfiguration();
        const auth = new AuthModule();
        await auth.loadAuthModule(setIsLoggedIn);
        setAuthInstance(auth);

        const clientConfiguration = getClient();
        setClient(clientConfiguration);
        const appInsights = new ApplicationInsights({
          config: {
            instrumentationKey: config?.settings?.appInsightsInstrumentationKey,
            enableAutoRouteTracking: true,
            enableCorsCorrelation: true,
          },
        });
        appInsights.loadAppInsights();
        setAppInsights(appInsights);
        // Stamp the frontend build id (injected into the runtime config by the
        // release pipeline) onto every telemetry item so App Insights records
        // which UI bundle a browser was running. Without this, exceptions such as
        // GraphQL schema-drift errors from stale cached bundles have a blank
        // application_Version and cannot be traced back to a build.
        appInsights.context.application.ver = config?.settings?.appBuildId ?? 'unknown';
        appInsights.trackPageView(); // Manually call trackPageView to establish the current user/session/pageview
      } catch (error) {
        setInitError(
          error instanceof Error ? error : new Error('Application initialization failed'),
        );
      }
    })();
    loadTheme(CoherenceTheme);
  }, []);

  useEffect(() => {
    if (isLoggedIn && client) {
      graphApiRequest(graphConfig.graphMePhotoEndpoint, false)
        .then((graphPhotoResponse) => {
          return graphPhotoResponse.blob();
        })
        .then((photoBlob) => {
          return photoBlob ? URL.createObjectURL(photoBlob) : null;
        })
        .then((imageUrlFromBlob) => {
          return graphApiRequest(graphConfig.graphMeEndpoint).then((profileResponse) => {
            setGraphProfileData({ userImageUrl: imageUrlFromBlob, graphProfile: profileResponse });
          });
        });
    }
  }, [isLoggedIn, client]);

  useEffect(() => {
    const { hostname } = window.location;
    const hostnameParts = hostname.toLocaleLowerCase().replace('https://', '').split('.');

    if (hostnameParts[0] !== 'stat') {
      setEnvironmentName(hostnameParts[0]);
    }
  });

  const onLogOut = (): void => {
    getAuthInstance()?.logout();
  };

  // Fix Tabster dummy elements that are aria-hidden but focusable (a11y violation).
  // FluentProvider internally creates these elements for focus management.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      document
        .querySelectorAll('[data-tabster-dummy][aria-hidden="true"][tabindex="0"]')
        .forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const initErrorClasses = useInitErrorStyles();

  if (initError) {
    return (
      <div className={initErrorClasses.container}>
        <FluentProvider theme={webLightTheme}>
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <strong>Application Initialization Failed</strong>
              <p>
                The application failed to initialize properly. Please try refreshing the page. If
                the problem persists, contact support.
              </p>
              <p className={initErrorClasses.errorText}>{initError.message}</p>
            </MessageBarBody>
          </MessageBar>
        </FluentProvider>
      </div>
    );
  }

  return (
    <>
      {client && (
        <ApolloProvider client={client}>
          <NotificationProvider
            value={{
              statNotifications,
              notifications,
              toasts,
              setNotifications,
              setStatNotifications,
              setToasts,
            }}
          >
            <Router>
              <a href="#main-content" className={skipLinkStyle}>
                Skip to main content
              </a>
              <StatHeader
                onLogOut={onLogOut}
                graphProfileData={graphProfileData?.graphProfile}
                imageUrl={graphProfileData?.userImageUrl}
                environmentName={environmentName}
              />
              {isLoggedIn && graphProfileData?.graphProfile?.id ? (
                <React.Suspense fallback={<Spinner label="Loading…" />}>
                  <Switch>
                    <Page
                      exact
                      path="/stat-contacts"
                      component={StatContactsNoAuthPage}
                      title="Stat Contacts"
                    />
                    <Route path="/authcallback" component={AuthCallback} />
                    <Route
                      path="/"
                      render={() => {
                        return (
                          <AuthorizationChecker>
                            <FluentProvider theme={webLightTheme}>
                              <App
                                setNotifications={setNotifications}
                                setStatNotifications={setStatNotifications}
                                userImageUrl={graphProfileData.userImageUrl}
                              />
                            </FluentProvider>
                          </AuthorizationChecker>
                        );
                      }}
                    />
                  </Switch>
                </React.Suspense>
              ) : (
                <div />
              )}
            </Router>
          </NotificationProvider>
        </ApolloProvider>
      )}
    </>
  );
};

export default AppLoader;
